import { Box, Button, createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import clsx from 'clsx';
import React from 'react';
import ReactGA from 'react-ga';
import { useTranslation } from 'react-i18next';

import LoadingContainer from '../../../../../components/LoadingContainer';
import { refreshTableFn } from '../../../../../components/TableGrid/TableGridApiContext/TableGridApiContexConstructor';
import {
  useTableGridApiDispatch,
  useTableGridApiState,
} from '../../../../../components/TableGrid/TableGridApiContext/TableGridApiContext';
import { CONSTANTS } from '../../../../../core/config/constants';
import { FilterBackgrounds, FontSizes } from '../../../../../core/config/mainTheme';
import {
  START_PAGINATION_PAGE,
  useCompaniesDispatch,
  useCompaniesState,
} from '../../../shared/CompanyContext/CompanyContext';
import { CompanyContextActionTypes } from '../../../shared/CompanyContext/CompanyContext.types';
import {
  useFilterDispatch,
  useFilterState,
} from '../../../shared/FinancialDataContext/FinancialDataContext';
import { FinancialDataContextActionTypes } from '../../../shared/FinancialDataContext/FinancialDataContext.types';

interface OwnProps {
  benchmarkRefinement: boolean;
  cancelRefinementLoading: boolean;
  setShowConfirm: React.Dispatch<React.SetStateAction<boolean>>;
  assistVisible?: boolean;
  setAssistVisible?: (v: boolean) => void;
  disableAssist?: boolean;
}

const FilterHeader: React.FC<OwnProps> = React.memo(
  ({
    benchmarkRefinement,
    cancelRefinementLoading,
    setShowConfirm,
    assistVisible,
    setAssistVisible,
    disableAssist,
  }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const dispatchCompanies = useCompaniesDispatch();
    const dispatchFilter = useFilterDispatch();
    const { tableGridApi } = useTableGridApiState();
    const dispatchTableApi = useTableGridApiDispatch();
    const { data, appliedFilters } = useFilterState();
    const { pagination, isLoading } = useCompaniesState();

    const handleSearchClick = () => {
      ReactGA.event({ category: 'SEARCH', action: 'SEARCH_CLICK' });
      dispatchCompanies({
        type: CompanyContextActionTypes.SetIsLoadingState,
        payload: true,
      });
      dispatchFilter({
        type: FinancialDataContextActionTypes.SetRequestFilterValues,
        payload: data,
      });
      dispatchCompanies({
        type: CompanyContextActionTypes.SetFilterButtonClickState,
        payload: true,
      });
      dispatchCompanies({
        type: CompanyContextActionTypes.SetPagination,
        payload: { ...pagination, page: START_PAGINATION_PAGE },
      });
      refreshTableFn(tableGridApi, dispatchTableApi);
    };

    return (
      <Box className={classes.root}>
        <Box className={classes.filtersLabel}>
          <Typography className={classes.filterContrastText}>
            {t('companies:filterCompanies')}
          </Typography>
        </Box>
        <Box className={classes.rightActionBox}>
          {/* Assist toggle button in header */}
          {!disableAssist && setAssistVisible && typeof assistVisible === 'boolean' && (
            <Box className={classes.actionItemBox}>
              <Button
                className={classes.assistToggleButtonHeader}
                onClick={() => setAssistVisible(!assistVisible)}
                startIcon={assistVisible ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              >
                {assistVisible ? 'Hide Assist' : 'Show Assist'}
              </Button>
            </Box>
          )}
          {benchmarkRefinement && (
            <Box className={classes.actionItemBox}>
              <Button
                className={clsx(classes.button, classes.cancelRefinementButton)}
                onClick={() => setShowConfirm(true)}
                disabled={!(appliedFilters.length && !isLoading && !cancelRefinementLoading)}
              >
                {cancelRefinementLoading ? (
                  <LoadingContainer size={20} progressColor="secondary" />
                ) : (
                  t('companies:cancelRefinement')
                )}
              </Button>
            </Box>
          )}
          <Box className={classes.actionItemBox}>
            <Button
              className={clsx(classes.button, classes.filterSubmitButton)}
              endIcon={<ArrowForwardIcon />}
              onClick={handleSearchClick}
              disabled={!(appliedFilters.length && !isLoading && !cancelRefinementLoading)}
            >
              {t('companies:filter')}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  },
);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.background.paper,
      height: CONSTANTS.TOOLBAR_HEIGHT,
      padding: theme.spacing(0, 0, 0, 6),
      justifyContent: 'space-between',
      position: 'relative',
      display: 'flex',
      alignItems: 'stretch',
    },
    filtersLabel: {
      display: 'flex',
      fontSize: FontSizes.size.headerSize,
      alignItems: 'center',
      '& .MuiTypography-root': {
        lineHeight: 1,
      },
      height: '44px',
    },
    rightActionBox: {
      marginLeft: 'auto',
      position: 'relative',
      display: 'flex',
    },
    actionItemBox: {
      display: 'flex',
    },
    filterContrastText: {
      fontSize: FontSizes.size.headerSize,
      color: theme.palette.primary.contrastText,
    },
    button: {
      borderRadius: 0,
      color: theme.palette.primary.contrastText,
    },
    filterSubmitButton: {
      backgroundColor: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
      },
    },
    cancelRefinementButton: {
      backgroundColor: FilterBackgrounds.FifthChildDark,
      '&:hover': {
        backgroundColor: FilterBackgrounds.FifthChildDark,
      },
      borderRight: `1px solid ${theme.palette.background.paper}`,
    },
    assistToggleButtonHeader: {
      textTransform: 'none',
      fontWeight: 500,
      fontSize: 14,
      padding: theme.spacing(0.5, 1),
      borderRadius: 16,
      background: 'transparent',
      color: theme.palette.primary.contrastText,
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2), // add right margin to move left
      minWidth: 0,
      boxShadow: 'none',
      height: 32,
      alignSelf: 'center',
      '& .MuiButton-startIcon': {
        marginRight: 4,
      },
      '&:hover': {
        background: theme.palette.action.hover,
      },
    },
  }),
);

export default FilterHeader;
